/**
 * The page load the browser is on, for request analytics.
 *
 * Every navigation begins a new one (router.ts), and every API call carries
 * it as two headers (api.ts), so the server can list page loads and the calls
 * each one made. The route's NAME only — never its query, a form's fields or
 * anything typed.
 *
 * Deliberately free of imports: api.ts reads this module, and a module that
 * api.ts imports must not import api.ts back.
 */

let current: { id: string; screen: string } | null = null;

/**
 * A random id. crypto.randomUUID exists only in secure contexts, and the dev
 * server is also reached over a LAN address, which is not one —
 * getRandomValues works everywhere.
 */
function newId(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

/** A route name as the server accepts it: lowercase letters, digits and hyphens. */
export function screenName(name: string): string {
  const clean = name.toLowerCase().replace(/[^a-z0-9-]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60);
  return clean || "unknown";
}

/** Start a new page load for this screen, and return its id. */
export function beginPageView(screen: string): string {
  current = { id: newId(), screen: screenName(screen) };
  return current.id;
}

/** The headers every API call carries while a page load is current. */
export function pageViewHeaders(): Record<string, string> {
  return current ? { "X-Genie-Page-View": current.id, "X-Genie-Page": current.screen } : {};
}
